import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ChefHat, Package, Store, Users, Percent } from "lucide-react";
import api from "@/api";
import { sym } from "@/currency";
import { useI18n } from "@/i18n";

export const openGlobalSearch = () => window.dispatchEvent(new CustomEvent("open-gsearch"));

export function GSearchTrigger({ mobile }) {
  const { t } = useI18n();
  return mobile ? (
    <button onClick={openGlobalSearch} className="p-1.5 rounded-lg hover:bg-muted" data-testid="global-search-btn-mobile"><Search className="w-5 h-5" /></button>
  ) : (
    <button onClick={openGlobalSearch}
      className="flex items-center gap-2 bg-white/90 backdrop-blur border border-border shadow-sm rounded-full px-3.5 py-1.5 text-sm text-muted-foreground hover:bg-muted transition-colors"
      data-testid="global-search-btn">
      <Search className="w-4 h-4" /> {t("globalSearchBtn")} <kbd className="mono text-[10px] bg-muted rounded px-1.5 py-0.5">Ctrl K</kbd>
    </button>
  );
}

export default function GlobalSearch() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen(true); }
      if (e.key === "Escape") setOpen(false);
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("open-gsearch", onOpen);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("open-gsearch", onOpen); };
  }, []);

  useEffect(() => {
    if (!open) { setQ(""); return; }
    setTimeout(() => inputRef.current?.focus(), 50);
    if (!data) {
      Promise.all([
        api.recipes().catch(() => []),
        api.ingredients().catch(() => []),
        api.posProducts().catch(() => []),
        api.partners().catch(() => []),
        api.formulaList().catch(() => []),
      ]).then(([recipes, ingredients, products, partners, formulas]) => setData({ recipes, ingredients, products, partners, formulas }));
    }
  }, [open]); // eslint-disable-line

  if (!open) return null;
  const ql = q.trim().toLowerCase();
  const m = (s) => (s || "").toLowerCase().includes(ql);
  const groups = !ql || !data ? [] : [
    { label: t("gsCatalog"), icon: ChefHat, route: "/app/catalog", rows: data.recipes.filter((r) => m(r.name) || m(r.sku) || m(r.category)).slice(0, 5).map((r) => ({ id: r.id, sku: r.sku, name: r.name, meta: `${sym()} ${(+r.selling_price || 0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}${r.available === false ? " · esaurito" : ""}` })) },
    { label: t("gsInventory"), icon: Package, route: "/app/inventory", rows: data.ingredients.filter((i) => m(i.name) || m(i.sku) || m(i.category) || m(i.supplier_name)).slice(0, 5).map((i) => ({ id: i.id, sku: i.sku, name: i.name, meta: `${t("stock")}: ${+(+i.stock).toFixed(2)} ${i.unit}` })) },
    { label: t("gsPos"), icon: Store, route: "/app/cassa", rows: data.products.filter((p) => m(p.name) || m(p.sku) || m(p.category)).slice(0, 5).map((p) => ({ id: p.id, sku: p.sku, name: p.name, meta: `${sym()} ${(+p.price || 0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}` })) },
    { label: t("gsFormulas"), icon: Percent, route: "/app/formula", rows: (data.formulas || []).filter((f) => m(f.name)).slice(0, 5).map((f) => ({ id: f.id, name: f.name, meta: `${(f.ingredients || []).length} ing.`, to: `/app/formula?open=${f.id}` })) },
    { label: t("gsPartners"), icon: Users, route: "/app/partners", rows: data.partners.filter((p) => m(p.name) || m(p.code)).slice(0, 5).map((p) => ({ id: p.id, sku: p.code, name: p.name, meta: p.kind === "supplier" ? t("supplier") : t("customer"), to: p.kind === "supplier" ? "/app/suppliers" : "/app/partners" })) },
  ].filter((g) => g.rows.length);

  return (
    <div className="fixed inset-0 bg-black/50 z-[80] flex items-start justify-center pt-[10vh] p-4" onClick={() => setOpen(false)} data-testid="global-search-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <Search className="w-4 h-4 text-muted-foreground shrink-0" />
          <input ref={inputRef} className="flex-1 outline-none text-sm bg-transparent" placeholder={t("gsPlaceholder")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="global-search-input" />
          <button onClick={() => setOpen(false)} data-testid="global-search-close"><X className="w-4 h-4 text-muted-foreground" /></button>
        </div>
        <div className="max-h-[55vh] overflow-y-auto" data-testid="global-search-results">
          {ql && data && groups.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground" data-testid="gs-no-results">{t("gsNoResults")}</div>}
          {!ql && <div className="p-6 text-center text-xs text-muted-foreground">{t("gsPlaceholder")}</div>}
          {groups.map((g) => (
            <div key={g.label}>
              <div className="px-4 pt-3 pb-1 label">{g.label}</div>
              {g.rows.map((r) => (
                <button key={r.id} onClick={() => { setOpen(false); nav(r.to || g.route); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2 hover:bg-muted text-left" data-testid={`gs-result-${r.id}`}>
                  <g.icon className="w-4 h-4 text-muted-foreground shrink-0" />
                  {r.sku && <span className="mono text-[10px] text-muted-foreground shrink-0">{r.sku}</span>}
                  <span className="text-sm font-medium flex-1 truncate">{r.name}</span>
                  <span className="text-xs text-muted-foreground mono shrink-0">{r.meta}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
